/**
 * ShadowCam getUserMedia capture profiles.
 *
 * Locked resolutions (CameraIO / DeviceProperties). frameRate on each profile is
 * the fallback used when the camera's selected rate cannot be opened:
 * - ShadowCam00 (bottom / questionable mobile): 640×360
 * - ShadowCam01 (middle / mobile): 896×480 (CameraIO `_856x480` advertises
 *   width after round_up_to_mult64(856) → 896; match must use advertised size)
 * - ShadowCam03 (top / desktop): 1280×720
 *
 * Capture policy: request the camera's selected frame rate exactly. If that open
 * fails, retry the same exact resolution preferring FALLBACK_CAPTURE_FPS and let
 * the device pick the rate it supports at that size (`getCapabilities().frameRate`
 * is device-wide, not per resolution). deviceId stays exact.
 */

import {
  QUALITY_TIER_RESOLUTION,
  STREAM_RESOLUTION_BY_DEVICE,
} from '@playcastdotio/PeerCore';

export type ShadowcamCaptureProfile = {
  width: number;
  height: number;
  frameRate: number;
};

/** Preferred (not required) rate when the camera's selected frame rate cannot be opened. */
export const FALLBACK_CAPTURE_FPS = 60;

/** Driver jitter around the rate that was requested. */
export const CAPTURE_FPS_TOLERANCE = 5;

export type ShadowcamConstraintMode = 'exact' | 'fallback';

/**
 * Resolutions come from PeerCore's pinned stream contract so capture and the
 * hardware-capability probe can never disagree about what this session encodes.
 */
export const SHADOWCAM_CAPTURE_PROFILES = {
  ShadowCam00: { ...QUALITY_TIER_RESOLUTION.bottom, frameRate: FALLBACK_CAPTURE_FPS },
  ShadowCam01: { ...STREAM_RESOLUTION_BY_DEVICE.mobile, frameRate: FALLBACK_CAPTURE_FPS },
  ShadowCam03: { ...STREAM_RESOLUTION_BY_DEVICE.desktop, frameRate: FALLBACK_CAPTURE_FPS },
} as const satisfies Record<string, ShadowcamCaptureProfile>;

export function normalizeShadowcamProfileName(shadowcamName: string): string {
  return Object.keys(SHADOWCAM_CAPTURE_PROFILES).find((name) => shadowcamName.includes(name)) ?? shadowcamName;
}

export function profileForShadowcamName(shadowcamName: string): ShadowcamCaptureProfile | null {
  const profileName = normalizeShadowcamProfileName(shadowcamName);
  if (profileName === 'ShadowCam00') {
    return SHADOWCAM_CAPTURE_PROFILES.ShadowCam00;
  }
  if (profileName === 'ShadowCam01') {
    return SHADOWCAM_CAPTURE_PROFILES.ShadowCam01;
  }
  if (profileName === 'ShadowCam03') {
    return SHADOWCAM_CAPTURE_PROFILES.ShadowCam03;
  }
  return null;
}

/**
 * Exact resolution of the named ShadowCam. `exact` requests `requestedFrameRate`
 * when the camera advertised one, and otherwise leaves the rate to the device.
 * `fallback` prefers FALLBACK_CAPTURE_FPS (`ideal`) so the open cannot fail on
 * rate alone. Rates are in frames per second. Unknown names produce only an
 * exact deviceId constraint.
 */
export function videoConstraintsForShadowcam(
  deviceId: string,
  shadowcamName: string,
  mode: ShadowcamConstraintMode = 'exact',
  requestedFrameRate?: number,
): MediaTrackConstraints {
  const base: MediaTrackConstraints = {
    deviceId: { exact: deviceId },
  };

  const profile = profileForShadowcamName(shadowcamName);
  if (!profile) {
    return base;
  }

  const size = {
    width: { exact: profile.width },
    height: { exact: profile.height },
  };
  if (mode === 'fallback') {
    return { ...base, ...size, frameRate: { ideal: FALLBACK_CAPTURE_FPS } };
  }
  if (requestedFrameRate && requestedFrameRate > 0) {
    return { ...base, ...size, frameRate: { exact: requestedFrameRate } };
  }
  return { ...base, ...size };
}

/** Accept finite FPS within 5 of a positive request; without a positive request, require only finite, positive FPS. */
export function captureFrameRateMatches(actual: number, requested: number): boolean {
  if (!(requested > 0)) return Number.isFinite(actual) && actual > 0;
  return Number.isFinite(actual) && Math.abs(actual - requested) <= CAPTURE_FPS_TOLERANCE;
}

function constraintNumber(
  value: ConstrainULong | ConstrainDouble | undefined,
  key: 'exact' | 'ideal' | 'min',
): number | undefined {
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object' && key in value) {
    const n = (value as Record<string, unknown>)[key];
    if (typeof n === 'number') return n;
  }
  return undefined;
}

/**
 * Read each dimension in exact, ideal, then min order and FPS in exact, then min
 * order (numeric values are used directly). An `ideal` FPS is a preference, not a
 * floor. Returns null without both dimensions; absent FPS becomes 0, meaning any
 * finite, positive frame rate during profile verification.
 */
export function captureFloorFromConstraints(
  constraints: MediaTrackConstraints,
): ShadowcamCaptureProfile | null {
  const width =
    constraintNumber(constraints.width, 'exact') ??
    constraintNumber(constraints.width, 'ideal') ??
    constraintNumber(constraints.width, 'min');
  const height =
    constraintNumber(constraints.height, 'exact') ??
    constraintNumber(constraints.height, 'ideal') ??
    constraintNumber(constraints.height, 'min');
  const frameRate =
    constraintNumber(constraints.frameRate, 'exact') ??
    constraintNumber(constraints.frameRate, 'min');

  if (width === undefined || height === undefined) {
    return null;
  }

  return { width, height, frameRate: frameRate ?? 0 };
}

/**
 * Named ShadowCam resolution at any finite positive rate. The exact open already
 * enforced the advertised rate; the fallback open legitimately runs at whatever
 * the device supports for that size, so the rate is not re-checked here.
 */
export function trackMeetsRequestedShadowcam(track: MediaStreamTrack, shadowcamName: string): boolean {
  const profile = profileForShadowcamName(shadowcamName);
  return !!profile && trackMeetsCaptureProfile(track, { ...profile, frameRate: 0 });
}

/** Match exact pixel dimensions and the requested FPS tolerance; ended tracks never match. */
export function trackMeetsCaptureProfile(
  track: MediaStreamTrack,
  profile: ShadowcamCaptureProfile,
): boolean {
  const settings = track.getSettings();
  const width = settings.width ?? 0;
  const height = settings.height ?? 0;
  const frameRate = settings.frameRate ?? 0;
  return (
    width === profile.width &&
    height === profile.height &&
    captureFrameRateMatches(frameRate, profile.frameRate) &&
    track.readyState !== 'ended'
  );
}

/**
 * True when the live track already satisfies resolution bounds and the fps floor in
 * `constraints`. Constraints without width/height/frameRate always match
 * (deviceId-only capture / OBS path).
 */
export function trackMatchesVideoConstraints(
  track: MediaStreamTrack,
  constraints: MediaTrackConstraints,
): boolean {
  const floor = captureFloorFromConstraints(constraints);
  if (!floor) return true;
  return trackMeetsCaptureProfile(track, floor);
}
